import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Button } from 'primeng/button';
import { InputText } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { FileUploadModule } from 'primeng/fileupload';
import { TableModule } from 'primeng/table';
import { Card } from 'primeng/card';
import { Checkbox } from 'primeng/checkbox';
import { CommonModule } from '@angular/common';
import { ControlErrorDirective } from '../../../../shared/directive/control-error.directive';
import { BaseFormComponent } from '../../../../core/components/base-form.component';
import { OrganizationSettingsAddMemberModel } from './form-group/organization-settings-add-member.model';
import { OrganizationSettingsAddMemberFormBuilder } from './form-group/organization-settings-add-member.form.builder';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import { OrganizacjaApi } from '../../../../shared/services/api/organizacja.api';
import {
  getRolaReprezentantaOrganizacjiDropdownItemsWithoutOwner,
  RolaReprezentantaOrganizacji,
} from '../../../../shared/enums/rola-reprezentanta-organizacji.enum';
import {
  catchError,
  distinctUntilChanged,
  EMPTY,
  filter,
  switchMap,
  tap,
} from 'rxjs';
import { ErrorMessagesPipe } from '../../../../shared/pipes/error-messages.pipe';
import { ErrorSummary } from '../../../../shared/components/error-summary/error-summary';
import { ZaproszenieDoOrganizacji } from '../../../../shared/models/custom-types';
import { ApplicationContextService } from '../../../../core/services/application-context.service';
import { HttpResponseService } from '../../../../core/services/http-error-response.service';

@Component({
  selector: 'app-organization-settings-add-member',
  imports: [
    CommonModule,
    Button,
    ControlErrorDirective,
    FormsModule,
    InputText,
    ReactiveFormsModule,
    Select,
    FileUploadModule,
    TableModule,
    Card,
    Checkbox,
    ErrorMessagesPipe,
    ErrorSummary,
  ],
  templateUrl: './organization-settings-add-member.component.html',
  styleUrl: './organization-settings-add-member.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [OrganizacjaApi],
})
export class OrganizationSettingsAddMemberComponent extends BaseFormComponent<OrganizationSettingsAddMemberModel> {
  readonly attachments = signal<{ file: File; uploadedAt: Date }[]>([]);

  protected readonly oganizationMemberRoleItems =
    getRolaReprezentantaOrganizacjiDropdownItemsWithoutOwner();
  protected readonly RolaReprezentantaOrganizacji =
    RolaReprezentantaOrganizacji;

  protected readonly dynamicDialogRef = inject(DynamicDialogRef);
  readonly #destroyRef = inject(DestroyRef);
  readonly #httpResponseService = inject(HttpResponseService);
  readonly #messageService = inject(MessageService);
  readonly #organizationApi = inject(OrganizacjaApi);
  readonly #applicationContextService = inject(ApplicationContextService);

  override ngOnInit(): void {
    super.ngOnInit();
    this.#subscribeToPeselValueChanges();
    this.#subscribeToRoleValueChanges();
  }

  protected onAddFile(event: any): void {
    const newFiles: File[] = Array.from(event.files || []);
    const maxSize = 1000000;

    for (const file of newFiles) {
      if (file.size > maxSize) {
        this.#messageService.add({
          severity: 'error',
          summary: 'Plik jest zbyt duży',
          detail: 'Maksymalny rozmiar pliku to 1 MB.',
        });
        continue;
      }

      this.attachments.update((arr) => [
        ...arr,
        { file, uploadedAt: new Date() },
      ]);
    }

    event?.options?.clear?.();
  }

  protected removeFile(i: number) {
    this.attachments.update((arr) => arr.filter((_, idx) => idx !== i));
  }

  protected downloadFile(entry: { file: File; uploadedAt: Date }) {
    const url = URL.createObjectURL(entry.file);
    const a = document.createElement('a');
    a.href = url;
    a.download = entry.file.name;
    a.click();
    URL.revokeObjectURL(url);
  }

  protected override createFormGroup(): FormGroup<OrganizationSettingsAddMemberModel> {
    return OrganizationSettingsAddMemberFormBuilder.createFormGroup(
      this.#applicationContextService.getOrganizationFromContext()!,
    );
  }

  protected addOrganizationMember(): void {
    this.formGroup().markAllAsTouched();
    this.formGroup().updateValueAndValidity();

    const emailCtrl = this.formGroup().controls.email;

    if (this.formGroup().invalid) {
      if (emailCtrl.enabled && emailCtrl.hasError('email')) {
        this.#messageService.add({
          severity: 'error',
          summary: 'Błąd walidacji formularza',
          detail: 'Niepoprawny adres e-mail',
          life: 5000,
        });
      }
      return;
    }

    if (this.formGroup().valid) {
      const role = this.formGroup().controls.rola.value;
      const instytucje: string[] = this.formGroup()
        .controls.instytucje.getRawValue()
        .filter((v) => v.selected)
        .map((v) => v.identyfikator!) as string[];

      const selectedOrganization =
        role === RolaReprezentantaOrganizacji.OUP ? instytucje : [];
      const daneZaproszenia: ZaproszenieDoOrganizacji = {
        pesel: this.formGroup().controls.pesel.value!,
        email: this.formGroup().controls.email.value!,
        rola: this.formGroup().controls.rola.value!,
        instytucje: selectedOrganization,
        pelnomocnictwa: this.attachments().map((a) => a.file),
      };

      this.dynamicDialogRef.close(daneZaproszenia);
    }
  }

  #subscribeToRoleValueChanges(): void {
    this.formGroup()
      .controls.rola.valueChanges.pipe(
        tap(() => {
          this.formGroup().controls.instytucje.controls.forEach((value) =>
            value.controls.selected.setValue(false),
          );
        }),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  #subscribeToPeselValueChanges(): void {
    const peselControl = this.formGroup().controls.pesel;
    const emailControl = this.formGroup().controls.email;
    peselControl.valueChanges
      .pipe(
        distinctUntilChanged(),
        tap(() => {
          if (peselControl.invalid) {
            emailControl.reset(
              { value: null, disabled: true },
              { emitEvent: false },
            );
          }
        }),
        filter(() => peselControl.valid),
        switchMap((pesel) => {
          emailControl.reset(
            { value: null, disabled: true },
            { emitEvent: false },
          );

          return this.#organizationApi.emailByPesel$(pesel!).pipe(
            tap((resp: any) => {
              const status = resp?.status;
              const email = resp?.body ?? resp;

              if (status === 204 || email == null || email === '') {
                emailControl.reset(
                  { value: null, disabled: false },
                  { emitEvent: false },
                );

                this.#messageService.add({
                  severity: 'info',
                  summary: 'Nowy użytkownik',
                  detail:
                    'Osoba nie została znaleziona. Wprowadź adres e-mail ręcznie.',
                  life: 5000,
                });
                return;
              }

              emailControl.reset(
                { value: email, disabled: true },
                { emitEvent: false },
              );

              this.#messageService.add({
                severity: 'success',
                summary: 'Adres e-mail znaleziony',
                detail: 'Adres e-mail został pobrany poprawnie.',
                life: 5000,
              });
            }),
            catchError((err) => {
              emailControl.reset(
                { value: null, disabled: true },
                { emitEvent: false },
              );
              this.#httpResponseService.showErrorMessage(
                err,
                'Nie udało się pobrać adresu e-mail dla podanego numeru PESEL',
              );
              return EMPTY;
            }),
          );
        }),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }
}
